"use client";

import { useSubmitConsultation } from "@/hooks/useContent";
import type { Locale } from "@/types/home";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useLocale } from "next-intl";
import { useEffect, useState } from "react";

const field =
  "w-full border border-black/15 bg-transparent p-3 text-sm text-ash outline-none transition-colors focus:border-accent dark:border-white/15 dark:text-white";

/**
 * Reusable consultation / booking modal — title + optional subtitle.
 * Submits a real lead to the backend. Pass `projectId`/`unitId` to target a
 * specific project/unit; otherwise it's filed as a general enquiry with the
 * title as its subject.
 */
export default function ConsultationModal({
  open,
  onClose,
  title,
  subtitle,
  projectId,
  unitId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  projectId?: string;
  unitId?: string;
}) {
  const ar = (useLocale() as Locale) === "ar";
  const submit = useSubmitConsultation();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", notes: "" });

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) { setSent(false); setForm({ name: "", phone: "", email: "", notes: "" }); }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const context = [title, subtitle].filter(Boolean).join(" — ");
    const message = [form.notes, context && (ar ? `بخصوص: ${context}` : `Re: ${context}`)].filter(Boolean).join(" | ");
    const targetType = unitId ? "unit" : projectId ? "project" : "general";
    try {
      await submit.mutateAsync({ name: form.name, phone: form.phone, email: form.email, message, targetType, projectId, unitId, subject: title });
      setSent(true);
    } catch {
      // keep the form open so the user can retry
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-6"
        >
          <motion.div
            onClick={(e) => e.stopPropagation()}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-black/10 bg-white p-8 dark:border-white/10 dark:bg-[#161616]"
          >
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <span className="text-[11px] font-semibold uppercase tracking-widest text-accent">
                  {ar ? "حجز استشارة" : "Book consultation"}
                </span>
                <h3 className="mt-1 font-bebas text-2xl uppercase text-ash dark:text-white">{title}</h3>
                {subtitle && (
                  <p className="text-[11px] uppercase tracking-widest text-ash/50 dark:text-white/50">{subtitle}</p>
                )}
              </div>
              <button onClick={onClose} aria-label={ar ? "إغلاق" : "Close"} className="text-ash/50 transition-colors hover:text-accent dark:text-white/50">
                <X className="h-6 w-6" />
              </button>
            </div>

            {sent ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 h-2 w-2 bg-accent" />
                <p className="font-semibold text-ash dark:text-white">{ar ? "تم استلام طلبك!" : "Request received!"}</p>
                <p className="mt-2 text-sm text-ash/60 dark:text-white/60">
                  {ar ? "سيتواصل معك مستشارنا قريباً." : "Our advisor will contact you shortly."}
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <input required placeholder={ar ? "الاسم الكامل" : "Full name"} className={field} value={form.name} onChange={set("name")} />
                <input required type="tel" placeholder={ar ? "رقم الهاتف" : "Phone number"} className={field} value={form.phone} onChange={set("phone")} />
                <input type="email" placeholder={ar ? "البريد الإلكتروني" : "Email"} className={field} value={form.email} onChange={set("email")} />
                <textarea rows={3} placeholder={ar ? "ملاحظات (اختياري)" : "Notes (optional)"} className={field} value={form.notes} onChange={set("notes")} />
                {submit.isError && (
                  <p className="border border-accent/30 bg-accent/5 px-4 py-3 text-[12px] font-semibold text-accent">
                    {submit.error instanceof Error ? submit.error.message : (ar ? "تعذّر الإرسال" : "Failed to send")}
                  </p>
                )}
                <button type="submit" disabled={submit.isPending} className="w-full bg-accent py-4 text-[12px] font-semibold uppercase tracking-widest text-white transition-colors hover:bg-ash disabled:opacity-60 dark:hover:bg-white dark:hover:text-ash">
                  {submit.isPending ? (ar ? "جارٍ الإرسال…" : "Sending…") : ar ? "إرسال الطلب" : "Send request"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
